import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import AppDialog from "../../Dialogs/AppDialog/AppDialog";
import { IKnowledgeArticleModel } from "../../../Services/KnowledgeArticleService";
import { isKnowledgeArticleRelationDisplayLink } from "../../../Pages/KnowledgeBase/knowledgeArticleRelationConstants";
import KnowledgeArticleEmbedDialog from "./KnowledgeArticleEmbedDialog";
import LinkedKnowledgeArticlesList from "./LinkedKnowledgeArticlesList";
import {
    LinkedKnowledgeArticlesTarget,
    LinkedKnowledgeArticlesTargetProps,
    useLinkedKnowledgeArticles,
} from "./useLinkedKnowledgeArticles";
import "./LinkedKnowledgeArticlesInfoButton.scss";

export type LinkedKnowledgeArticlesInfoTarget = LinkedKnowledgeArticlesTarget;

/** Page-header help for the dialog-mode articles linked to this overview or item: one article opens
 *  straight in the embed dialog, several open a list first. In-page articles are not this button's —
 *  they belong to {@code LinkedKnowledgeArticlesInPage}. Hidden when there is nothing to show. */
const LinkedKnowledgeArticlesInfoButton: React.FC<LinkedKnowledgeArticlesTargetProps> = (props) => {
    const { t } = useTranslation();
    const articles = useLinkedKnowledgeArticles(props);
    const [listVisible, setListVisible] = useState(false);
    const [embedArticle, setEmbedArticle] = useState<IKnowledgeArticleModel | null>(null);

    const dialogArticles = articles.filter(
        (article) => !isKnowledgeArticleRelationDisplayLink(article.relationDisplayMode)
    );
    const title = t("knowledgeBase.relations.linkedArticles") as string;

    const openDialogHelp = () => {
        if (dialogArticles.length === 1) {
            setEmbedArticle(dialogArticles[0]);
            return;
        }
        setListVisible(true);
    };

    if (dialogArticles.length === 0) {
        return null;
    }

    return (
        <>
            <Button
                type="button"
                text
                rounded
                icon="pi pi-info-circle"
                className="linked-knowledge-articles-info-button"
                aria-label={title}
                tooltip={title}
                tooltipOptions={{ position: "bottom" }}
                onClick={openDialogHelp}
            />
            <AppDialog
                visible={listVisible}
                onHide={() => setListVisible(false)}
                header={title}
                className="linked-knowledge-articles-info-dialog"
                style={{ width: "min(92vw, 28rem)" }}
                footerActions={{
                    cancelLabel: t("common.action.cancel") as string,
                    onCancel: () => setListVisible(false),
                    submitLabel: t("common.action.close") as string,
                    onSubmit: () => setListVisible(false),
                }}
            >
                <LinkedKnowledgeArticlesList articles={dialogArticles} />
            </AppDialog>
            <KnowledgeArticleEmbedDialog
                articleUuid={embedArticle?.uuid ?? null}
                sectionIdentifiers={embedArticle?.relationSectionIdentifiers}
                onHide={() => setEmbedArticle(null)}
            />
        </>
    );
};

export default LinkedKnowledgeArticlesInfoButton;
